import React from "react";
import SectionContainer from "../../components/SectionContainer";
import Reveal from "../../components/Reveal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComment,
  faClipboardCheck,
  faTools,
  faLifeRing,
  faProjectDiagram,
  faChartBar,
  faArrowRight,
  faCog,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

const iconMap = {
  Consultation: faComment,
  Planning: faClipboardCheck,
  Execution: faTools,
  Support: faLifeRing,
  Optimization: faChartBar,
  Scaling: faProjectDiagram,
};

const colorMap = {
  Consultation: "from-blue-500 to-indigo-600",
  Planning: "from-green-500 to-emerald-600",
  Execution: "from-orange-500 to-red-600",
  Support: "from-purple-500 to-violet-600",
  Optimization: "from-cyan-500 to-blue-500",
  Scaling: "from-pink-500 to-rose-600",
};

const operations = [
  {
    title: "Consultation",
    description:
      "We begin by deeply understanding your goals, challenges, and success metrics through comprehensive analysis.",
    icon: "Consultation",
    step: "01",
    duration: "1-2 weeks",
    deliverable: "Strategy Blueprint",
  },
  {
    title: "Planning",
    description:
      "Strategic roadmap aligning technology, timelines, and measurable outcomes with your business objectives.",
    icon: "Planning",
    step: "02",
    duration: "2-3 weeks",
    deliverable: "Project Roadmap",
  },
  {
    title: "Execution",
    description:
      "Agile implementation using modern frameworks and best practices for velocity and quality delivery.",
    icon: "Execution",
    step: "03",
    duration: "8-12 weeks",
    deliverable: "MVP Solution",
  },
  {
    title: "Support",
    description:
      "Ongoing assistance, monitoring, and iteration to ensure sustained performance and user satisfaction.",
    icon: "Support",
    step: "04",
    duration: "Ongoing",
    deliverable: "24/7 Support",
  },
  {
    title: "Optimization",
    description:
      "Data-driven improvements enhance scalability, reliability, and efficiency through continuous monitoring.",
    icon: "Optimization",
    step: "05",
    duration: "Continuous",
    deliverable: "Performance Reports",
  },
  {
    title: "Scaling",
    description:
      "We help expand capabilities across regions, teams, and product lines with proven growth strategies.",
    icon: "Scaling",
    step: "06",
    duration: "3-6 months",
    deliverable: "Scale Framework",
  },
];

const OperationModelSection = () => {
  return (
    <SectionContainer id="operationmodel" variant="alt">
      <div className="relative">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden opacity-5">
          <svg className="w-full h-full" viewBox="0 0 100 100">
            <defs>
              <pattern
                id="operation-pattern"
                width="25"
                height="25"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="12.5" cy="12.5" r="1" fill="currentColor" />
                <path
                  d="M 25 12.5 L 12.5 12.5 L 12.5 0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#operation-pattern)" />
          </svg>
        </div>

        {/* Header Section */}
        <div className="text-center mb-20 relative z-10">
          <div className="inline-flex items-center px-6 py-3 bg-[#E5C558]/10 rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/20">
            <FontAwesomeIcon icon={faCog} className="mr-2 animate-spin" />
            Proven Process
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8 leading-tight">
            Our{" "}
            <span className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] bg-clip-text text-transparent">
              Operation Model
            </span>
          </h2>
          <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            Built for adaptability, velocity and measurable impact across teams
            with proven methodologies and industry best practices.
          </p>
        </div>

        {/* Process Flow Visualization */}
        <div className="hidden lg:flex justify-center items-center mb-16 relative z-10">
          <div className="flex items-center space-x-8">
            {operations.map((operation, index) => (
              <div key={index} className="flex items-center">
                <div className="relative">
                  <div
                    className={`w-16 h-16 rounded-full bg-gradient-to-br ${
                      colorMap[operation.icon]
                    } flex items-center justify-center shadow-lg`}
                  >
                    <FontAwesomeIcon
                      icon={iconMap[operation.icon] || faProjectDiagram}
                      className="text-white text-xl"
                    />
                  </div>
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
                      {operation.step}
                    </span>
                  </div>
                </div>
                {index < operations.length - 1 && (
                  <FontAwesomeIcon
                    icon={faArrowRight}
                    className="text-[#E5C558] mx-4 animate-pulse"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Operation Cards Grid */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 relative z-10">
          {operations.map((operation, index) => (
            <Reveal key={index} delay={index * 0.1}>
              <div className="group relative bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-700 overflow-hidden hover:-translate-y-2">
                {/* Background Gradient */}
                <div
                  className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${
                    colorMap[operation.icon]
                  } opacity-10 rounded-full transform translate-x-16 -translate-y-16 group-hover:opacity-20 transition-opacity duration-500`}
                ></div>

                {/* Step Number */}
                <div className="absolute top-6 right-6">
                  <div
                    className={`w-12 h-12 rounded-full bg-gradient-to-br ${
                      colorMap[operation.icon]
                    } flex items-center justify-center shadow-md`}
                  >
                    <span className="text-white font-bold text-lg">
                      {operation.step}
                    </span>
                  </div>
                </div>

                {/* Icon */}
                <div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${
                    colorMap[operation.icon]
                  } flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                >
                  <FontAwesomeIcon
                    icon={iconMap[operation.icon] || faProjectDiagram}
                    className="text-white text-2xl"
                  />
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-[#E5C558] transition-colors duration-300">
                    {operation.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                    {operation.description}
                  </p>

                  {/* Process Details */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Duration:
                      </span>
                      <span className="text-sm font-semibold text-[#E5C558]">
                        {operation.duration}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Key Deliverable:
                      </span>
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {operation.deliverable}
                      </span>
                    </div>
                  </div>

                  {/* Progress Indicator */}
                  <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-green-500 mr-2"
                      />
                      <span>Step {operation.step} of 6</span>
                    </div>
                  </div>
                </div>

                {/* Hover Effect Indicator */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Success Metrics */}
        <div className="mt-20 relative z-10">
          <div className="bg-gradient-to-r from-[#E5C558]/10 to-[#d4b54d]/10 rounded-3xl p-12 border border-[#E5C558]/20 backdrop-blur-sm">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                Proven Results Across Industries
              </h3>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Our operation model has consistently delivered exceptional
                outcomes for clients worldwide.
              </p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#E5C558] mb-2">
                  98%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  On-Time Delivery
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#E5C558] mb-2">
                  150+
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Successful Projects
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#E5C558] mb-2">
                  35%
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Avg. Cost Reduction
                </div>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold text-[#E5C558] mb-2">
                  24/7
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Support Coverage
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <a
                href="/contact-us"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-white font-bold rounded-full hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Start Your Journey
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default OperationModelSection;
